import { useState, useMemo } from 'react';

import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';

import { buildMediaUrl } from '../Services/mediaUrl';

const asSrc = (im) => {
  // tolerate multiple shapes: string, {base64}, {url}, {src}
  if (!im) return '';
  if (typeof im === 'string') return im; // already a data: url
  return im.base64 || im.url || im.src || buildMediaUrl(im) || '';
};

const ImageGallery = ({ message }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const galleryImages = useMemo(() => {
    const imgs = Array.isArray(message?.images) ? message.images : [];
    return imgs
      .map((im, i) => {
        const src = asSrc(im);
        return src
          ? {
              src, // prefer data: if available
              thumbnail: src,
              downloadSrc: buildMediaUrl(im, 'files', true) || src,
              isSelected: false,
              alt: `Image ${i + 1}`,
            }
          : null;
      })
      .filter(Boolean);
  }, [message?.images]);

  if (galleryImages.length === 0) return null;

  // Treat missing/boolean/string values uniformly
  const galleryFlag = message?.gallery;
  const isGallery =
    galleryFlag === true ||
    galleryFlag === 'true' ||
    (galleryFlag == null && galleryImages.length > 1);

  const isSingle =
    galleryFlag === false ||
    galleryFlag === 'false' ||
    (galleryFlag == null && galleryImages.length === 1);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setIsGalleryOpen(true);
  };

  const handleDownloadClick = (e, src, filename) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = src;
    link.download = filename || 'image.jpg';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // chunk helper
  const chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  return (
    <div className="media-container" data-gallery="react">
      {/* Single image */}
      {isSingle && (
        <div
          className="single-image-container"
          onClick={() => openLightbox(0)}
          style={{ position: 'relative', overflow: 'hidden' }}
        >
          <img src={galleryImages[0].src} alt="Media" className="single-image" />

          <div className="image-hover-overlay">
            <button
              type="button"
              className="download-image-btn"
              onClick={(e) => handleDownloadClick(e, galleryImages[0].downloadSrc, 'image-1.jpg')}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Grid gallery (3 per row on your CSS) */}
      {isGallery && (
        <div className="custom-gallery-container">
          {chunk(galleryImages, 3).map((row, rIdx) => (
            <div key={rIdx} className={`gallery-row col-${row.length}`}>
              {row.map((it, cIdx) => {
                const idx = rIdx * 3 + cIdx;
                return (
                  <div
                    key={idx}
                    className={`gallery-item item-${idx}`}
                    onClick={() => openLightbox(idx)}
                    style={{ position: 'relative', overflow: 'hidden' }}
                  >
                    <img src={it.src} alt="Media" className="single-image" />

                    <div className="image-hover-overlay">
                      <button
                        type="button"
                        className="download-image-btn"
                        onClick={(e) =>
                          handleDownloadClick(e, it.downloadSrc, `image-${idx + 1}.jpg`)
                        }
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <Lightbox
        open={isGalleryOpen}
        close={() => setIsGalleryOpen(false)}
        slides={galleryImages}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        plugins={[Zoom]}
        zoom={{ maxZoomPixelRatio: 5 }}
        styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' } }}
        render={{
          slidePrevButton: galleryImages.length > 1 ? undefined : () => null,
          slideNextButton: galleryImages.length > 1 ? undefined : () => null,
        }}
        carousel={{
          finite: galleryImages.length === 1,
        }}
      />
    </div>
  );
};

export default ImageGallery;
